import { TreeData } from 'components/GenericTree';
import { join, initial, split, pipe } from 'lodash/fp';
import { find, reduce } from 'lodash';
import { ApiItem, ItemId, TreeItem } from '../types';

const nestingLevel = (item: ApiItem) => item.path.split('/').length;
const isNotRootLevel = (item: ApiItem) => item.path.includes('/');

const getRootChildren = (input: Array<ApiItem>) =>
  input.reduce<string[]>(
    (acc, item) => (isNotRootLevel(item) ? acc : [...acc, item.path]),
    [],
  );

const createEntry = (
  item: ApiItem,
  children: string[],
  isExpanded: boolean,
) => {
  // A temporary item must go into edit mode. The state logic makes
  // sure that only one such item will be present.
  const isPersisted = Object.prototype.hasOwnProperty.call(item, 'isPersisted')
    ? item.isPersisted
    : true;
  const isFolder = item.is_directory || item.type === 'dir';

  return {
    id: item.path,
    isFolder,
    children,
    isBeingEdited: !isPersisted,
    hasChildren: isFolder && Boolean(children.length),
    isExpanded: Boolean(isExpanded || item.isExpanded),
    isChildrenLoading: false,
    isPersisted,
    data: {
      title: item.name,
    },
  };
};

const createRoot = (input: Array<ApiItem>): TreeData => ({
  rootId: '0-internal-root-folder-for-tree',
  items: {
    '0-internal-root-folder-for-tree': {
      children: getRootChildren(input),
      data: {
        title: 'root',
      },
      hasChildren: true,
      id: '0-internal-root-folder-for-tree',
      isChildrenLoading: false,
      isExpanded: true,
      isPersisted: true,
    },
  },
});

export const getPath = pipe(
  split('/'),
  initial,
  join('/'),
);

const isContained = (child: ApiItem, item: ApiItem) =>
  getPath(child.path) === item.path;

const isChildOf = (child: ApiItem, item: ApiItem) =>
  isNotRootLevel(child) &&
  nestingLevel(item) + 1 === nestingLevel(child) &&
  isContained(child, item) &&
  item.is_directory;

function comparePaths(a: ApiItem, b: ApiItem) {
  if (a.path < b.path) {
    return -1;
  }
  if (a.path > b.path) {
    return 1;
  }
  return 0;
}

function compareTypes(a: ApiItem, b: ApiItem) {
  if (a.type < b.type) {
    return -1;
  }
  if (a.type > b.type) {
    return 1;
  }
  return 0;
}

/**
 * Given an item, based on its `id` property, it will construct an array with
 * all parent paths. For example, given an `id` of 'hello/workd/file.txt' it
 * will return the array `['hello', 'hello/world', 'hello/workd/file.txt'].
 *
 * @param treeItem
 */
export function getParentPaths(treeItem: TreeItem) {
  const pathFragments: Array<string> = treeItem.id.split('/').reverse();

  return pathFragments
    .map((fragment, index, array) => {
      const parentPathFragments = array.slice(index + 1).reverse();
      if (parentPathFragments.length > 0) {
        return [...parentPathFragments, fragment].join('/');
      }
      return fragment;
    })
    .reverse();
}

/**
 * Based on the paths given, the related tree items will be expanded.
 *
 * @param treeItems - An object containing tree items and item ids as properties.
 * @param paths - An array of paths found in the tree that must be expanded.
 */
export function expandItems(
  treeItems: Record<ItemId, TreeItem>,
  paths: string[],
): Record<ItemId, TreeItem> {
  return reduce(
    treeItems,
    (result, item, itemId) => {
      if (paths.includes(itemId)) {
        return {
          ...result,
          [itemId]: { ...item, isExpanded: true },
        };
      }
      return { ...result, [itemId]: item };
    },
    {},
  );
}

/**
 * Given an array of API objects, it will construct the structure needed for the
 * tree component. In addition, if there is a temporary item inside then it will
 * expand all its parent folders.
 *
 * @param input - An array of file or folder API objects.
 * @param expandAllItems
 */
export const normalize = (
  input: Array<ApiItem>,
  expandAllItems = false,
): TreeData => {
  const sortedInput = input.sort(comparePaths).sort(compareTypes);

  const match = input.reduce<TreeData>((acc, item) => {
    const children = sortedInput.reduce<string[]>(
      (acc, child) => (isChildOf(child, item) ? [...acc, child.path] : acc),
      [],
    );

    return {
      ...acc,
      items: {
        ...acc.items,
        [item.path]: {
          ...createEntry(item, children, expandAllItems),
        },
      },
    };
  }, createRoot(input));

  const tempItem = find(match.items, { isPersisted: false });
  if (tempItem) {
    const parentPaths = getParentPaths(tempItem);
    match.items = expandItems(match.items, parentPaths);
  }

  return match;
};

export default normalize;
